import { logout } from '@/mixins/logout'
import router from '@/router'
import store from '@/store'

const INACTIVITY_TIMEOUT_MS = parseInt(
  process.env.VUE_APP_INACTIVITY_TIMEOUT_MS || '300000',
  10,
)
const WARNING_BEFORE_MS = 60 * 1000

const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'click',
]

const LAST_ACTIVITY_KEY = 'ff_last_activity'
// Where the user was when auto-logged-out, so the next login returns them there.
const RETURN_PATH_KEY = 'ff_return_path'

let timer: ReturnType<typeof setTimeout> | null = null
let warningTimer: ReturnType<typeof setTimeout> | null = null
let loggingOut = false
let lastReset = 0

export function stopInactivityTimer(): void {
  ACTIVITY_EVENTS.forEach((event) =>
    window.removeEventListener(event, resetTimer),
  )
  window.removeEventListener('storage', handleStorageEvent)
  if (warningTimer !== null) {
    clearTimeout(warningTimer)
    warningTimer = null
  }
  if (timer !== null) {
    clearTimeout(timer)
    timer = null
  }
}

async function performLogout(): Promise<void> {
  if (loggingOut) return
  loggingOut = true
  stopInactivityTimer()
  // Capture before logout (it navigates to '/'); write after (logout clears
  // localStorage). Login restores it on the next successful sign-in.
  const returnPath = router.currentRoute.fullPath
  try {
    await logout.methods.logout()
    if (returnPath && returnPath !== '/') {
      localStorage.setItem(RETURN_PATH_KEY, returnPath)
    }
  } finally {
    loggingOut = false
  }
}

function showWarning(): void {
  store.dispatch('showSnackbar', {
    message: 'You will be logged out due to inactivity in 1 minute.',
    color: 'warning',
    icon: 'timer',
    timeout: WARNING_BEFORE_MS,
  })
}

// checkAndLogout is defined before scheduleTimers to avoid forward references.
// It inlines its own reschedule logic to break the circular dependency.
function checkAndLogout(): void {
  const lastActivity = parseInt(
    localStorage.getItem(LAST_ACTIVITY_KEY) || '0',
    10,
  )
  const elapsed = Date.now() - lastActivity
  const remaining = INACTIVITY_TIMEOUT_MS - elapsed

  if (remaining > 0) {
    // Another tab was active — reschedule based on actual last activity
    if (warningTimer !== null) clearTimeout(warningTimer)
    if (timer !== null) clearTimeout(timer)
    const warningDelay = remaining - WARNING_BEFORE_MS
    if (warningDelay > 0) warningTimer = setTimeout(showWarning, warningDelay)
    timer = setTimeout(checkAndLogout, remaining)
    return
  }

  performLogout()
}

function scheduleTimers(delay: number): void {
  if (warningTimer !== null) clearTimeout(warningTimer)
  if (timer !== null) clearTimeout(timer)
  const warningDelay = delay - WARNING_BEFORE_MS
  if (warningDelay > 0) warningTimer = setTimeout(showWarning, warningDelay)
  timer = setTimeout(checkAndLogout, delay)
}

function resetTimer(): void {
  const now = Date.now()
  if (now - lastReset < 1000) return
  lastReset = now
  localStorage.setItem(LAST_ACTIVITY_KEY, now.toString())
  scheduleTimers(INACTIVITY_TIMEOUT_MS)
}

function handleStorageEvent(event: StorageEvent): void {
  if (event.key === LAST_ACTIVITY_KEY) {
    const lastActivity = parseInt(event.newValue || '0', 10)
    const remaining = INACTIVITY_TIMEOUT_MS - (Date.now() - lastActivity)
    if (remaining > 0) {
      lastReset = Date.now()
      scheduleTimers(remaining)
    }
    return
  }

  // Another tab logged out — follow immediately
  if (event.key === 'access_token' && event.newValue === null) {
    stopInactivityTimer()
    if (router.currentRoute.path !== '/') {
      router.push('/')
    }
  }
}

export function startInactivityTimer(): void {
  loggingOut = false
  ACTIVITY_EVENTS.forEach((event) =>
    window.addEventListener(event, resetTimer, { passive: true }),
  )
  window.addEventListener('storage', handleStorageEvent)
  resetTimer()
}
